<style src="./Editor.scss" lang="scss"></style>

<template lang="pug">
  form.editor(
    :class='classList'
    @submit.prevent='() => {}'
  )
    .editor__header
      .editor__cover
        ImageUpload(
          :image='getAlbumCover()'
          @update-cover='updateAlbumCover'
        )
      .editor__fields
        .editor__radiobuttons
          .editor__radiobutton
            Radiobutton(
              name='type'
              field-value='single'
              v-model='form.type'
              :label='$l10n("SINGLE")'
            )
          .editor__radiobutton
            Radiobutton(
              name='type'
              field-value='album'
              v-model='form.type'
              :label='$l10n("ALBUM")'
            )
        .editor__field
          Input(
            name='name'
            :placeholder='$l10n("ALBUM_NAME")'
            :required='true'
            v-model='form.title'
          )
        .editor__fields-row
          .editor__field.is-short
            Input(
              :placeholder='$l10n("YEAR")'
              name='year'
              mask='####'
              v-model='form.year'
            )
    .editor__body
      .editor__directions
        .editor__direction(
          v-for='direction in directions'
          v-if='direction.items.length'
          :key='direction.id'
        )
          GenreSelect(
            :direction='direction'
            :genres='getAlbumGenres(direction)'
            @change='list => updateAlbumGenres(direction.id, list)'
          )
      .editor__tracks-upload
        TracksUpload(
          :title='form.is_single ? $l10n("TRACK") : $l10n("TRACKS")'
          :tracks='form.tracks'
          :multiple='!form.is_single'
          :tracksLimit='form.is_single ? 1 : 30'
          :upload-handler='uploadAlbumTrack'
          @sort='sortAlbumTracks'
          @track-rename='onTrackRename'
          @checked='checkTrack'
        )
    .editor__footer
      p.editor__text {{ $l10n("SAVE_OR_CANCEL_CHANGES") }}
      .editor__buttons
        .editor__button
          Button(
            @click='saveAlbum'
            :label='$l10n("SAVE")'
            type='button'
            class-list='is-small is-rounded'
          )
        .editor__button
          Button(
            @click='cancel'
            :label='$l10n("CANCEL")'
            type='button'
            class-list='is-small is-bordered is-rounded is-grey'
          )
        .editor__button()
          Button(
            @click='deleteAlbum'
            :title='$l10n("DELETE")'
            type='button'
            class-list='is-small is-bordered is-rounded is-grey has-icon'
          )
            Icon(
              name='delete'
            )
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import Input from "@/components/Input/Input";
import MultiSelect from "@/components/MultiSelect/MultiSelect";
import Icon from "@/components/Icon/Icon";
import Button from "@/components/Button/Button";
import Radiobutton from "@/components/Radiobutton/Radiobutton";
import ImageUpload from "@/components/ImageUpload/ImageUpload";
import TracksUpload from "@/components/TracksUpload/TracksUpload";
import GenreSelect from "@/components/GenreSelect/GenreSelect";

import editorMixin from "@/mixins/components/editor.mixin";

export default {
  props: {
    album: {
      type: Object,
      required: true
    },
    directions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      genresByDirections: {},
      deletedTracks: []
    };
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (this.form.type === "single" && this.form.tracks.length > 1) {
          this.$error(this.$l10n("SINGLE_MUST_HAVE_ONE_TRACK"));
          setTimeout(() => {
            this.form.type = "album";
          }, 400);
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      genres: "genres/genres",
      tracks: "tracks/tracks"
    }),
    albumGenresIds() {
      return this.album.genres ? this.album.genres.map(genre => genre.id) : [];
    },
    albumTracksIds() {
      return this.album.tracks || [];
    },
    albumTracks() {
      return this.albumTracksIds
        .map(id => this.tracks[id])
        .filter(track => Boolean(track));
    }
  },
  components: {
    Input,
    Icon,
    Button,
    Radiobutton,
    ImageUpload,
    TracksUpload,
    GenreSelect,
    MultiSelect
  },
  mixins: [editorMixin],
  methods: {
    ...mapActions({
      save: "albums/save",
      delete: "albums/delete",
      uploadTrack: "albums/uploadTrack"
    }),
    async checkSaveAccess(force) {
      if (force) return true;

      let accessed = true;

      if (this.deletedTracks.length) {
        const { confirm = false } = await this.openModal({
          name: "confirm",
          data: {
            title: this.$l10n("CONTINUE"),
            caption:
              this.deletedTracks.length > 1
                ? this.$l10n("SELECTED_TRACKS_WILL_DELETED")
                : this.$l10n("SELECTED_TRACK_WILL_DELETED")
          }
        });

        accessed = confirm;
      }

      return accessed;
    },
    async saveAlbum(form = this.form, force = false) {
      if (this.hasChanges) {
        if (await this.checkSaveAccess(force)) {
          this.loading = true;
          this.hasChanges = false;

          await this.save({
            form: {
              ...form,
              tracks: form.tracks.filter(
                track => !this.deletedTracks.includes(track.id)
              )
            },
            preventBack: force
          });

          this.loading = false;
        }
      } else {
        await this.cancel();
      }
    },
    async deleteAlbum() {
      this.hasChanges = false;

      const { deleted } = await this.delete({
        id: this.album.id,
        type: "full"
      });

      this.hasChanges = !deleted;
    },
    async uploadAlbumTrack(track) {
      const newTrack = await this.uploadTrack({
        id: this.album.id,
        track
      });

      this.form.tracks = this.getTracks([
        ...this.getFormTracksIds(),
        newTrack.id
      ]);

      return newTrack;
    },
    onTrackRename() {
      this.form.tracks = this.getTracks(this.getFormTracksIds());
    },
    updateAlbumCover(images) {
      if (images.length) {
        try {
          this.form.image = images[0].file;
          this.hasChanges = true;
        } catch (error) {
          this.form.image = null;
        }
      }
    },
    updateAlbumGenres(directionId, genres = []) {
      if (directionId) {
        this.genresByDirections[directionId] = genres.reduce(
          (genresIds, genre) => {
            return genre.selected ? [...genresIds, genre.id] : genresIds;
          },
          []
        );
      }

      this.form.genres = this.getSelectedGenres();
    },
    sortAlbumTracks(tracksIds) {
      this.form.tracks = this.getTracks(tracksIds);
    },
    checkTrack({ checked }) {
      this.deletedTracks = checked;

      if (this.deletedTracks.length) {
        this.hasChanges = true;
      }
    },
    getSelectedGenres() {
      return Object.keys(this.genresByDirections).reduce((genres, id) => {
        return [...genres, ...this.genresByDirections[id]];
      }, []);
    },
    getFormTracksIds() {
      return this.form.tracks.map(track => track.id);
    },
    getTracks(tracksIds = []) {
      return tracksIds
        .map(trackId => {
          return this.tracks[trackId] ? this.tracks[trackId] : null;
        })
        .filter(track => Boolean(track));
    },
    getForm() {
      return {
        id: this.album.id,
        title: this.album.title,
        type: this.album.type,
        year: this.album.year,
        image: null,
        genres: this.getSelectedGenres(),
        tracks: this.getTracks(this.album.tracks)
      };
    },
    getAlbumCover() {
      if (this.album !== null && this.album.cover) {
        return this.album.cover.medium;
      }

      return null;
    },
    getAlbumGenres(direction) {
      const genres = direction.items.reduce((genres, genreId) => {
        const selected = this.albumGenresIds.includes(genreId);
        const genre = {
          ...this.genres[genreId],
          selected
        };

        return [...genres, genre];
      }, []);

      return genres;
    },
    async restore() {
      await this.saveAlbum(this.initialForm, true);
    },
    cancel() {
      this.$router
        .push(`/albums/${this.album.id}${this.album.active ? "" : "/review"}`)
        .catch(() => {});
    }
  },
  created() {
    this.genresByDirections = this.directions.reduce((map, direction) => {
      const genres = direction.items.filter(genreId => {
        return this.albumGenresIds.includes(genreId);
      });

      return genres.length ? { ...map, [direction.id]: genres } : map;
    }, {});

    this.initialForm = this.getForm();
    this.form = this.getForm();
  }
};
</script>
